import { FormEvent, KeyboardEvent, MouseEvent, useState, Key, ReactNode, useMemo } from 'react'
import cx from 'classnames'
import { Checkbox } from 'screens/Finder/components/Checkbox/Checkbox'
import { Tag } from 'common/OptionSelect/Tag'
import { PostStatusPropertyValue } from 'api/clients/PropertyClient'
import { Pressable, useHover } from '@react-aria/interactions'
import { ContextMenuTrigger } from 'almanac-ui/ContextMenuTrigger/ContextMenuTrigger'
import { useModals } from 'almanac-ui/Modal'
import { Menu } from 'components/Menu/Menu'
import { Item } from 'components/Menu/MenuItem'
import { DesignMenuContainer } from 'components/DesignMenu/DesignMenuContainer'
import { DocMenuContainer } from 'components/DocMenu/DocMenuContainer'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { DesignChannel } from 'components/NewFileModal/Types'
import { FullScreenPreviewWithCarousel } from 'screens/Calendar/FullScreenPreview/FullScreenPreviewWithCarousel'
import { useHistory, generatePath } from 'react-router-dom'
import { DESIGN_PATH, DOC_PATH } from 'paths'
import { AssetType } from 'hooks/useAlmanacFileSubscription'
import { PostAtCell } from './PostAtCell/PostAtCell'
import { TitleCell } from './TitleCell/TitleCell'
import { useQueryClient } from 'react-query'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { IconButton } from 'components/IconButton/IconButton'
import { LinkAngled, MoreDots, AlertTriangle } from 'blaze-ui/icons'
import { useRootStore } from 'stores/RootStore'
import { DocMenuDrawer } from 'blaze-components/MobileWriterTopBar/DocMenuDrawer'
import { DesignMenuDrawer } from 'components/DesignMenu/DesignMenuDrawer'
import { Drawer } from 'components/Drawer'
import { Heading } from 'blaze-ui/Heading/Heading'
import { DrawerProps } from 'components/Drawer/Root'
import { MenuProps } from 'components/Menu/Types'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { ScheduledPostTarget } from 'types'
import { InitiateOauthTrigger } from 'components/ConnectIntegrationTrigger/InitiateOauthTrigger'
import { getProviderForChannel } from 'components/ArticlePostPreview/utils'
import { useConnectAccountPopup } from 'components/Onboarding/useConnectAccountPopup'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { useBlogIntegrations } from 'hooks/useBlogIntegrations'
import { useEmailIntegrations } from 'hooks/useEmailIntegrations'

import Styles from './ListRow.module.scss'
import { AlmanacFileIndexItem } from 'hooks/queries/files/types'
import { useUpdateFile } from 'hooks/queries/files/useUpdateFile'
import { optimisticRemoveFilesFromInfiniteLists } from 'hooks/queries/files/cacheUtils'
import { filesKeys } from 'hooks/queries/files/filesKeys'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'
import {
  STATUS_COLOR_V2,
  STATUS_FONT_COLOR_V2,
} from 'screens/ScheduledPosts/MobileCalendar/components/CalendarItem/CalendarItem'
import { useCurrentFolderStore } from 'hooks/stores/useCurrentFolderStore'
import { useAuth } from 'hooks/UseAuth'

const DOC_MENU_HIDDEN_KEYS: string[] = [
  'toggleVersionControl',
  'switchView',
  'readHistory',
  'toggleFocus',
  'export',
  'readInsights',
  'publish',
]

interface MobileMenuDrawerProps extends DrawerProps {
  renderContent: (close: () => void) => ReactNode
  title?: string
}

const MobileMenuDrawer = ({ renderContent, title = 'More Options', ...props }: MobileMenuDrawerProps) => {
  const { drawersStore } = useRootStore()
  const handleClose = () => drawersStore.closeDrawer()

  return (
    <Drawer.Root {...props}>
      <Drawer.Portal>
        <Drawer.Overlay withBlur={false} />
        <Drawer.Content>
          <Drawer.InnerContent>
            <Drawer.Top>
              <div />
              <Drawer.Title asChild>
                <Heading level={5}>{title}</Heading>
              </Drawer.Title>
              <Drawer.Close label="Close" variant="ghost" position="right" />
            </Drawer.Top>
            <Drawer.Container>{renderContent(handleClose)}</Drawer.Container>
          </Drawer.InnerContent>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  )
}

interface ListRowProps {
  item: AlmanacFileIndexItem
  index: number
  isSelected: boolean
  onSelect?: (isShiftKey: boolean) => void
  onRename?: (item: AlmanacFileIndexItem, newTitle: string) => void
  onContextMenu?: (item: AlmanacFileIndexItem, event: MouseEvent<HTMLDivElement>) => void
  hideSelectionColumn?: boolean
}

const STATUS_FONT_COLOR: Record<PostStatusPropertyValue, string> = {
  [PostStatusPropertyValue.Draft]: '#344054',
  [PostStatusPropertyValue.ReminderScheduled]: '#157EEF',
  [PostStatusPropertyValue.AutoPostScheduled]: '#157EEF',
  [PostStatusPropertyValue.Posted]: '#07942A',
  [PostStatusPropertyValue.Failed]: '#BC010B',
}

const STATUS_COLOR: Record<PostStatusPropertyValue, string> = {
  [PostStatusPropertyValue.Draft]: '#EAEDF1',
  [PostStatusPropertyValue.ReminderScheduled]: '#EFF7FF',
  [PostStatusPropertyValue.AutoPostScheduled]: '#EFF7FF',
  [PostStatusPropertyValue.Posted]: '#ECFDF3',
  [PostStatusPropertyValue.Failed]: '#FDECEC',
}

const STATUS_LABEL: Record<PostStatusPropertyValue, string> = {
  [PostStatusPropertyValue.Draft]: 'Draft',
  [PostStatusPropertyValue.ReminderScheduled]: 'Reminder Set',
  [PostStatusPropertyValue.AutoPostScheduled]: 'Scheduled',
  [PostStatusPropertyValue.Posted]: 'Posted',
  [PostStatusPropertyValue.Failed]: 'Failed',
}

const DEFAULT_STATUS_COLOR = '#E4E7EC'
const DEFAULT_STATUS_FONT = '#3F4856'

const POSTING_STATUS_COLOR = '#EFE6FF'
const POSTING_STATUS_FONT = '#7A5AF8'

const normalizeStatusValue = (status: string) =>
  status
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, '')

const getPostStatusValue = (status: string): PostStatusPropertyValue | null => {
  const normalized = normalizeStatusValue(status)

  const match = Object.values(PostStatusPropertyValue).find((value) => normalizeStatusValue(value) === normalized)
  if (match) return match

  if (normalized === 'autoscheduled' || normalized === 'autopostscheduled') {
    return PostStatusPropertyValue.AutoPostScheduled
  }

  return null
}

export const StatusTag = ({
  status,
  isPosting,
  scheduledPostTargets,
}: {
  status?: string
  isPosting?: boolean
  scheduledPostTargets?: ScheduledPostTarget[]
}) => {
  const isSystemRedesignEnabled = useIsSystemRedesign()
  const targetAnalysis = useMemo(() => {
    if (!scheduledPostTargets?.length) {
      return null
    }

    const successfulTargets = scheduledPostTargets.filter((target) => target.status === 'published')
    const failedTargets = scheduledPostTargets.filter((target) => target.status === 'failed')

    const hasAnySuccess = successfulTargets.length > 0
    const hasAnyFailure = failedTargets.length > 0

    return {
      isPureFailure: hasAnyFailure && !hasAnySuccess,
      isHybrid: hasAnySuccess && hasAnyFailure,
      isPureSuccess: hasAnySuccess && !hasAnyFailure,
      successfulTargets,
      failedTargets,
    }
  }, [scheduledPostTargets])

  if (isPosting) {
    return (
      <div className={Styles.statusTag} data-row-interactive="true">
        <Tag color={POSTING_STATUS_COLOR} fontColor={POSTING_STATUS_FONT}>
          Posting...
        </Tag>
      </div>
    )
  }

  if (!status) {
    return (
      <span className={Styles.statusPlaceholder} data-row-interactive="true">
        —
      </span>
    )
  }

  const postStatus = getPostStatusValue(status)

  if (!postStatus) {
    return (
      <div className={Styles.statusTag} data-row-interactive="true">
        <Tag
          color={STATUS_COLOR_V2[status] ?? DEFAULT_STATUS_COLOR}
          fontColor={STATUS_FONT_COLOR_V2[status] ?? DEFAULT_STATUS_FONT}
        >
          {status}
        </Tag>
      </div>
    )
  }

  // Hybrid logic: override status display based on target analysis
  let displayStatus = postStatus
  if (targetAnalysis) {
    if (targetAnalysis.isPureFailure) {
      displayStatus = PostStatusPropertyValue.Failed
    } else if (targetAnalysis.isHybrid || targetAnalysis.isPureSuccess) {
      displayStatus = PostStatusPropertyValue.Posted
    }
  }

  return (
    <div className={Styles.statusTag} data-row-interactive="true">
      <Tag
        color={isSystemRedesignEnabled ? STATUS_COLOR_V2[displayStatus] : STATUS_COLOR[displayStatus]}
        fontColor={isSystemRedesignEnabled ? STATUS_FONT_COLOR_V2[displayStatus] : STATUS_FONT_COLOR[displayStatus]}
        data-testid={`finder-v2-status-${normalizeStatusValue(displayStatus)}`}
      >
        {STATUS_LABEL[displayStatus]}
      </Tag>
      {targetAnalysis?.isHybrid && (
        <Tooltip
          label={`${targetAnalysis.successfulTargets.length} successful, ${targetAnalysis.failedTargets.length} failed`}
          placement="top"
        >
          <div className={Styles.hybridAlertIcon}>
            <AlertTriangle size={16} color="var(--negative-60)" />
          </div>
        </Tooltip>
      )}
    </div>
  )
}

interface ConnectTagProps {
  provider?: ExternalIntegrationEnum
  onInitiatedOauth?: (popUp?: Window) => void
}

export const ConnectTag = ({ provider, onInitiatedOauth }: ConnectTagProps) => {
  const renderTag = () => (
    <Tag color={'#FFF9EF'} fontColor={'#D38E0F'}>
      <div className={Styles.connectTagText}>
        <LinkAngled size={16} color={'#D38E0F'} />
        Connect
      </div>
    </Tag>
  )

  if (!provider || !onInitiatedOauth) {
    return (
      <div className={Styles.statusTag} data-row-interactive="true">
        {renderTag()}
      </div>
    )
  }
  return (
    <div className={Styles.statusTag} data-row-interactive="true">
      <InitiateOauthTrigger popUp provider={provider} onInitiatedOauth={onInitiatedOauth}>
        <Pressable>{renderTag()}</Pressable>
      </InitiateOauthTrigger>
    </div>
  )
}

export const ListRow = ({
  item,
  index,
  isSelected,
  onSelect,
  onRename,
  onContextMenu,
  hideSelectionColumn = false,
}: ListRowProps) => {
  const [isRenaming, setIsRenaming] = useState(false)
  const [draftTitle, setDraftTitle] = useState(item.title ?? '')
  const { hoverProps, isHovered } = useHover({})
  const { currentFolder } = useCurrentFolderStore()
  const auth = useAuth()
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()
  const { isMobile } = useResponsiveDesign()
  const { drawersStore } = useRootStore()
  const { openModal, closeModal } = useModals()
  const history = useHistory()
  const { connect } = useConnectAccountPopup()
  const { connectedBlogIntegration } = useBlogIntegrations()
  const { connectedEmailIntegration } = useEmailIntegrations()
  const scheduledTargetProvider = (item.scheduled_post_targets ?? []).find((target) => target.provider)?.provider
  const channelProvider = item.channel ? getProviderForChannel(item.channel) : undefined
  const provider = scheduledTargetProvider ?? (channelProvider as ExternalIntegrationEnum | undefined)
  const isBlogPost = item.channel === ChannelType.BlogPost
  const isNewsletter = item.channel === ChannelType.Newsletter
  const hasConnectedBlogIntegration = isBlogPost && !!connectedBlogIntegration
  const hasConnectedEmailForNewsletter = isNewsletter && !!connectedEmailIntegration
  const canEdit = currentFolder?.has_edit_access || !currentFolder
  const hasNoConnectedTargets =
    auth &&
    canEdit &&
    (item.scheduled_post_targets ?? []).filter((t) => !!t.external_integration_id).length === 0 &&
    !hasConnectedBlogIntegration &&
    !hasConnectedEmailForNewsletter

  const hasActivePublishingTargets = (item.scheduled_post_targets ?? []).some((target) =>
    ['queued', 'processing', 'retrying'].includes(target.status),
  )

  const handleOpenPreview = () => {
    if (isMobile) {
      if (item.asset_type === AssetType.Article) {
        const docPath = generatePath(DOC_PATH, { slug: item.key })
        history.push(docPath)
      } else if (item.asset_type === AssetType.Design) {
        const designPath = generatePath(DESIGN_PATH, { key: item.key })
        history.push(designPath)
      }
      return
    }

    openModal(FullScreenPreviewWithCarousel, {
      id: item.key,
      channel: item.channel as ChannelType | DesignChannel,
      showCarousel: false,
      filters: {},
      onClose: closeModal,
      startDate: item.post_at,
    })
  }

  const handleDeleteSuccess = () => {
    if (!workspaceId) return
    optimisticRemoveFilesFromInfiniteLists(queryClient, workspaceId, [item.key])
  }

  const { mutate: updateFile } = useUpdateFile(item.key)

  const handleInitiatedOauth = (popUp?: Window) => {
    if (!popUp) return
    connect(popUp, {
      onConnect: () => {
        queryClient.invalidateQueries(filesKeys.all(workspaceId))
        queryClient.invalidateQueries(['externalIntegrations', workspaceId])
      },
    })
  }

  const handleCheckboxClick = (event: MouseEvent<HTMLInputElement>) => {
    event.preventDefault()
    event.stopPropagation()
    onSelect?.(event.shiftKey)
  }

  const handleRenameSubmit = (event?: FormEvent<HTMLFormElement>) => {
    event?.preventDefault()
    setIsRenaming(false)
    const trimmed = draftTitle.trim()
    if (trimmed && trimmed !== item.title) {
      updateFile({ title: trimmed })
    } else {
      setDraftTitle(item.title)
    }
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleRenameSubmit()
    }
    if (event.key === 'Escape') {
      event.preventDefault()
      setIsRenaming(false)
      setDraftTitle(item.title ?? '')
    }
  }

  const handleContextMenu = (event: MouseEvent<HTMLDivElement>) => {
    onContextMenu?.(item, event)
  }

  const handleRowClick = (event: MouseEvent<HTMLDivElement>) => {
    if (isMobile || isRenaming) {
      return
    }

    if (event.defaultPrevented) {
      return
    }

    const target = event.target as HTMLElement | null
    if (!target) {
      return
    }

    if (
      target.closest(
        'button, a, input, textarea, select, [role="button"], [role="checkbox"], [data-row-interactive="true"]',
      )
    ) {
      return
    }

    handleOpenPreview()
  }

  const renderContextMenu = (close: () => void, menuType: MenuProps<unknown>['menuType'] = 'dropdown') => {
    const handleFallbackAction = (key: Key) => {
      if (key === 'rename') {
        setIsRenaming(true)
      }
      close()
    }

    if (item.asset_type === 'Design') {
      return (
        <DesignMenuContainer
          id={item.key}
          dismiss={close}
          menuType={menuType}
          onMoveSuccess={handleDeleteSuccess}
          onRename={() => {
            setIsRenaming(true)
          }}
          onAction={(key) => {
            if (key === 'rename') {
              setIsRenaming(true)
            }
            close()
          }}
          onDeleteSuccess={handleDeleteSuccess}
        />
      )
    }

    if (item.asset_type === 'Article') {
      return (
        <DocMenuContainer
          menuType={menuType}
          id={item.key}
          dismiss={close}
          onMoveSuccess={handleDeleteSuccess}
          onAction={(key) => {
            if (key === 'rename') {
              setIsRenaming(true)
            }
            close()
          }}
          onDeleteSuccess={handleDeleteSuccess}
        />
      )
    }

    return (
      <Menu aria-label="File actions" menuType={menuType} onAction={handleFallbackAction}>
        <Item key="rename">Rename</Item>
      </Menu>
    )
  }

  const showCheckbox = onSelect && (isSelected || isHovered || isRenaming) && auth && canEdit

  const renderPreview = () => {
    if (item.image_url) {
      return <img className={Styles.preview} src={item.image_url} alt={item.title} />
    }
    if (item.content) {
      return (
        <div className={Styles.textPreviewContainer}>
          <div className={Styles.textPreviewTitle}>{item.title}</div>
          <div className={Styles.textPreview}>{item.content}</div>
        </div>
      )
    }
    return <div className={Styles.previewPlaceholder} />
  }

  const handleMobileActionsPress = () => {
    if (!isMobile) return

    const normalizedAssetType = item.asset_type?.toLowerCase()

    if (normalizedAssetType === 'design') {
      drawersStore.openDrawer(DesignMenuDrawer, {
        id: item.key,
        onRename: () => setIsRenaming(true),
        onDeleteSuccess: handleDeleteSuccess,
      })
      return
    }

    if (normalizedAssetType === 'article') {
      drawersStore.openDrawer(DocMenuDrawer, {
        articleId: item.key,
        hiddenKeys: DOC_MENU_HIDDEN_KEYS,
        onRename: () => setIsRenaming(true),
        onDeleteSuccess: handleDeleteSuccess,
      })
      return
    }

    drawersStore.openDrawer(MobileMenuDrawer, {
      renderContent: (close) => renderContextMenu(close, 'tray'),
    })
  }

  return (
    <ContextMenuTrigger placement="bottom-start">
      <div
        className={cx(Styles.row, { [Styles.selected]: isSelected, [Styles.hideSelectionColumn]: hideSelectionColumn })}
        role="row"
        onClick={handleRowClick}
        onContextMenu={handleContextMenu}
        data-index={index}
        {...hoverProps}
      >
        {!isMobile && !hideSelectionColumn && (
          <div
            className={cx(Styles.cell, Styles.indexCell, Styles.checkboxCell)}
            role="gridcell"
            aria-label={`Row ${index + 1}`}
          >
            {showCheckbox ? (
              <Checkbox id={`finder-v2-row-${item.key}`} checked={isSelected} onClick={handleCheckboxClick} visible />
            ) : (
              <span className={Styles.rowNumber}>{index + 1}</span>
            )}
          </div>
        )}

        {!isMobile && (
          <div className={cx(Styles.cell, Styles.previewCell)} role="gridcell">
            {renderPreview()}
          </div>
        )}

        <TitleCell
          item={item}
          isRenaming={isRenaming}
          draftTitle={draftTitle}
          onRename={(newTitle) => onRename?.(item, newTitle)}
          onStartRename={() => setIsRenaming(true)}
          onDraftTitleChange={setDraftTitle}
          onRenameSubmit={handleRenameSubmit}
          onKeyDown={handleKeyDown}
          onOpenPreview={handleOpenPreview}
        />

        {isMobile && (
          <div className={cx(Styles.cell, Styles.mobileActions)} role="gridcell">
            {item.post_status ? (
              <div className={Styles.mobileStatus}>
                <StatusTag
                  status={item.post_status}
                  isPosting={hasActivePublishingTargets}
                  scheduledPostTargets={item.scheduled_post_targets}
                />
              </div>
            ) : null}
            <IconButton
              icon={MoreDots}
              aria-label="More options"
              size="sm"
              onPress={handleMobileActionsPress}
              onClick={(event) => {
                event.preventDefault()
                event.stopPropagation()
              }}
            />
          </div>
        )}

        {!isMobile && (
          <div className={cx(Styles.cell, Styles.postAtCell)} role="gridcell">
            <PostAtCell item={item} fileChannel={item.channel as ChannelType | DesignChannel} />
          </div>
        )}

        {!isMobile && (
          <div className={cx(Styles.cell, Styles.statusCell)} role="gridcell">
            {hasNoConnectedTargets ? (
              <ConnectTag provider={provider} onInitiatedOauth={provider ? handleInitiatedOauth : undefined} />
            ) : (
              <StatusTag
                status={item.post_status ?? undefined}
                isPosting={hasActivePublishingTargets}
                scheduledPostTargets={item.scheduled_post_targets}
              />
            )}
          </div>
        )}
      </div>
      {(close) => renderContextMenu(close)}
    </ContextMenuTrigger>
  )
}
